import { ColumnDef } from '@tanstack/react-table'
import { CustomerMailer } from '@/modules/Customer/types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import EditIcons from '@/icons/EditIcons'
import { ArrowUpDown, MoreHorizontal } from 'lucide-react'

interface ColumnContext {
    t: (key: string) => string
    onEdit: (mailer: CustomerMailer) => void
    onEmailClick?: (email: string) => void
    selectedEmail?: string | null
}

export const createColumns = ({
    t,
    onEdit,
    onEmailClick,
    selectedEmail,
}: ColumnContext): ColumnDef<CustomerMailer>[] => [
    {
        id: 'select',
        header: ({ table }) => (
            <Checkbox
                checked={
                    table.getIsAllPageRowsSelected() ||
                    (table.getIsSomePageRowsSelected() && 'indeterminate')
                }
                onCheckedChange={(value) =>
                    table.toggleAllPageRowsSelected(!!value)
                }
                aria-label="Select all"
            />
        ),
        cell: ({ row }) => (
            <Checkbox
                checked={row.getIsSelected()}
                onCheckedChange={(value) => row.toggleSelected(!!value)}
                aria-label="Select row"
            />
        ),
        enableSorting: false,
        enableHiding: false,
    },
    {
        accessorKey: 'customer_mailer_name',
        header: ({ column }) => {
            return (
                <Button
                    variant="ghost"
                    onClick={() =>
                        column.toggleSorting(column.getIsSorted() === 'asc')
                    }
                    className="hover:bg-transparent p-0"
                >
                    {t('mailer-name')}
                    <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
            )
        },
        cell: ({ row }) => {
            const mailer = row.original
            return (
                <div className="flex items-center gap-2">
                    <div
                        className={`w-2 h-2 rounded-full ${
                            mailer?.sendable ? 'bg-green-500' : 'bg-red-500'
                        } animate-pulse`}
                    />
                    <span className="font-medium">
                        {mailer.customer_mailer_name}
                    </span>
                </div>
            )
        },
    },
    {
        accessorKey: 'sendable',
        header: t('status'),
        cell: ({ row }) => {
            const mailer = row.original
            return (
                <Badge variant={mailer?.sendable ? 'default' : 'destructive'}>
                    {mailer?.sendable ? t('active') : t('inactive')}
                </Badge>
            )
        },
    },
    {
        id: 'mailer_types',
        header: t('mailer_type_ids'),
        cell: ({ row }) => {
            const mailer = row.original
            return (
                <div className="space-y-1 min-w-[200px]">
                    {mailer?.mailer_types?.slice(0, 2).map((type, i) => (
                        <div
                            key={i}
                            className="text-sm text-muted-foreground truncate"
                        >
                            {type.mailer_description}
                        </div>
                    ))}
                    {mailer?.mailer_types && mailer.mailer_types.length > 2 && (
                        <span className="text-xs text-muted-foreground">
                            +{mailer.mailer_types.length - 2} {t('more')}
                        </span>
                    )}
                </div>
            )
        },
    },
    {
        id: 'recipients',
        header: t('recipients'),
        cell: ({ row }) => {
            const mailer = row.original
            return (
                <div className="flex flex-wrap gap-1 min-w-[200px]">
                    {mailer?.mailer_receiver
                        ?.split(',')
                        .slice(0, 2)
                        ?.map((email, i) => (
                            <Badge
                                key={i}
                                variant={
                                    selectedEmail === email.trim()
                                        ? 'default'
                                        : 'outline'
                                }
                                className="cursor-pointer text-xs"
                                onClick={() => onEmailClick?.(email.trim())}
                            >
                                {email.trim()}
                            </Badge>
                        ))}
                    {mailer?.mailer_receiver &&
                        mailer.mailer_receiver.split(',').length > 2 && (
                            <span className="text-xs text-muted-foreground">
                                +{mailer.mailer_receiver.split(',').length - 2}
                            </span>
                        )}
                </div>
            )
        },
    },
    {
        id: 'services',
        header: t('linked-services'),
        cell: ({ row }) => {
            const mailer = row.original
            if (!mailer?.services || mailer.services.length === 0) {
                return <span className="text-sm text-muted-foreground">-</span>
            }
            return (
                <div className="space-y-1 min-w-[150px]">
                    {mailer.services
                        .slice(0, 2)
                        .map((service: any, i: number) => (
                            <div
                                key={i}
                                className="text-sm text-muted-foreground truncate"
                            >
                                {service.service_name ||
                                    service.product_group_name ||
                                    `Service ${service.service_id}`}
                            </div>
                        ))}
                    {mailer.services.length > 2 && (
                        <span className="text-xs text-muted-foreground">
                            +{mailer.services.length - 2}
                        </span>
                    )}
                </div>
            )
        },
    },
    {
        id: 'actions',
        cell: ({ row }) => {
            const mailer = row.original
            return (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                            <span className="sr-only">Open menu</span>
                            <MoreHorizontal className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        <DropdownMenuLabel>{t('actions')}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => onEdit(mailer)}>
                            <EditIcons className="h-4 w-4" />
                            {t('edit')}
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )
        },
    },
]
